import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for managing sidebar visibility state
 *
 * Features:
 * - Window resize handling
 *
 * @returns {object} Sidebar state and controls
 */
export function useSidebarState() {
  // Initialize state: always start closed on new window/session
  const [isVisible, setIsVisible] = useState(false);

  // Handle responsive behavior on window resize
  useEffect(() => {
    const handleResize = () => {
      // Auto-close on mobile, but don't auto-open on desktop
      // to respect user's explicit choice
      if (window.innerWidth < 768 && isVisible) {
        setIsVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible]);

  // Toggle function
  const toggleSidebar = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  // Show/hide functions for explicit control
  const showSidebar = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hideSidebar = useCallback(() => {
    setIsVisible(false);
  }, []);

  return {
    isVisible,
    toggleSidebar,
    showSidebar,
    hideSidebar
  };
}
